/**
 * Brokerage contract status → presentation config (shared, non-component).
 *
 * Status values come from the shared enum in @dallali/shared:
 *   pending | active | expiring | expired | renew
 * Tolerant of unknown values: a new server status falls back to "Unknown"
 * rather than leaking the raw enum (#1699 tolerance rule).
 *
 * Lives in its own module (not BrokerageStatusBadge.tsx) so plain-text surfaces
 * — e.g. a task's notes snapshot — can translate a status the same way the
 * badge does, without importing a component (keeps Fast Refresh happy).
 */
import type { BrokerageContractStatus } from "@dallali/shared";

export type BrokerageBadgeVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "success"
  | "warning";

interface StatusConfig {
  ar: string;
  en: string;
  variant: BrokerageBadgeVariant;
}

const STATUS_CONFIG: Record<BrokerageContractStatus, StatusConfig> & {
  fallback: StatusConfig;
} = {
  active: { ar: "نشط", en: "Active", variant: "success" },
  expiring: { ar: "ينتهي قريباً", en: "Expiring Soon", variant: "warning" },
  expired: { ar: "منتهي", en: "Expired", variant: "destructive" },
  pending: { ar: "معلق", en: "Pending", variant: "secondary" },
  renew: { ar: "تجديد", en: "Renewal", variant: "outline" },
  fallback: { ar: "غير محدد", en: "Unknown", variant: "outline" },
};

/** Presentation config for a status, tolerant of unknown values. */
export function brokerageStatusConfig(status: string): StatusConfig {
  return (
    STATUS_CONFIG[status as BrokerageContractStatus] ?? STATUS_CONFIG.fallback
  );
}

/** Bilingual label for a brokerage contract status (tolerant of unknown values). */
export function brokerageStatusLabel(
  status: string,
  t: (ar: string, en: string) => string,
): string {
  const config = brokerageStatusConfig(status);
  return t(config.ar, config.en);
}
